import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PinVerifyDialog from './PinVerifyDialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Clock, Coffee, LogIn, LogOut, ChevronDown, ChevronUp} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PinRequirements } from '../types';
import { addToQueue, getCachedData, cacheData } from '../lib/offlineDb';
import { getApiErrorMessage } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface TimeClockWidgetProps {
    pinRequirements?: PinRequirements;
}

export default function TimeClockWidget({ pinRequirements }: TimeClockWidgetProps) {
    // @ts-expect-error TS6133 — intentional unused (kept for future use)
    const { _user } = useAuth();
    const [status, setStatus] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [punching, setPunching] = useState(false);
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [pendingPunch, setPendingPunch] = useState<{ eventType: string; action: string } | null>(null);
    const [minimized, setMinimized] = useState(() => localStorage.getItem('timeclock_minimized') === 'true');
    const [elapsed, setElapsed] = useState('');

    const fetchStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/timeclock/status`);
            setStatus(res.data);
            await cacheData('timeclock-status', res.data, 'timeclock');
        } catch {
            // Offline fallback
            const cached = await getCachedData('timeclock-status');
            if (cached) setStatus(cached);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // Elapsed time ticker
    useEffect(() => {
        if (!status?.shift_clocked_in || !status?.shift_entry?.clock_in) {
            setElapsed('');
            return;
        }
        const update = () => {
            const start = new Date(status.shift_entry.clock_in);
            const diff = Date.now() - start.getTime();
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setElapsed(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [status]);

    const doPunch = async (eventType: string, action: string, pinTok: string | null) => {
        setPunching(true);
        const punchData = { event_type: eventType, action, local_date: new Date().toISOString().split('T')[0] };
        try {
            if (!navigator.onLine) {
                await addToQueue({
                    method: 'POST',
                    url: `${API}/timeclock/punch`,
                    body: punchData,
                    headers: pinTok ? { 'X-PIN-Token': pinTok } : {},
                    module: 'timeclock',
                    description: `${eventType === 'shift' ? 'Shift' : 'Lunch'} ${action === 'in' ? 'clock in' : 'clock out'}`,
                });
                const label = eventType === 'shift' ? 'Shift' : 'Lunch';
                toast.success(`${label} ${action === 'in' ? 'clocked in' : 'clocked out'} (queued offline)`);
                // Optimistically update local status
                if (eventType === 'shift' && action === 'in') {
                    setStatus((prev: any) => ({ ...prev, shift_clocked_in: true, shift_entry: { clock_in: new Date().toISOString() } }));
                } else if (eventType === 'shift' && action === 'out') {
                    setStatus((prev: any) => ({ ...prev, shift_clocked_in: false, shift_entry: null }));
                } else if (eventType === 'lunch' && action === 'in') {
                    setStatus((prev: any) => ({ ...prev, on_lunch: true }));
                } else if (eventType === 'lunch' && action === 'out') {
                    setStatus((prev: any) => ({ ...prev, on_lunch: false }));
                }
            } else {
                await axios.post(
                    `${API}/timeclock/punch`,
                    punchData,
                    { headers: pinTok ? { 'X-PIN-Token': pinTok } : {} }
                );
                const label = eventType === 'shift' ? 'Shift' : 'Lunch';
                toast.success(`${label} ${action === 'in' ? 'clocked in' : 'clocked out'}`);
                await fetchStatus();
            }
        } catch (err: any) {
            toast.error(getApiErrorMessage(err, 'Clock punch failed'));
        } finally {
            setPunching(false);
        }
    };

    const handlePunch = (eventType: string, action: string) => {
        const needPin = !!pinRequirements?.edit_task;
        if (!needPin) {
            doPunch(eventType, action, null);
            return;
        }
        setPendingPunch({ eventType, action });
        setPinDialogOpen(true);
    };

    const onPinVerified = async (pinTok: string) => {
        if (pendingPunch) {
            await doPunch(pendingPunch.eventType, pendingPunch.action, pinTok);
            setPendingPunch(null);
        }
    };

    if (loading) return null;

    const shiftIn = status?.shift_clocked_in;
    const lunchIn = status?.lunch_clocked_in;

    const toggleMinimize = () => {
        setMinimized(prev => {
            localStorage.setItem('timeclock_minimized', String(!prev));
            return !prev;
        });
    };

    return (
        <>
            <Card className="border-primary/30 bg-primary/5" data-testid="time-clock-widget">
                <CardContent className={minimized ? "p-3" : "p-4 space-y-3"}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-sm">Time Clock</span>
                            {minimized && shiftIn && (
                                <Badge variant="default" className="text-[10px] h-5 px-1.5">In</Badge>
                            )}
                            {minimized && elapsed && (
                                <span className="text-xs font-mono text-muted-foreground">{elapsed}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {!minimized && (
                                <Link to="/timecard" className="text-xs text-primary hover:underline" data-testid="view-timecard-link">
                                    View Timecard
                                </Link>
                            )}
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={toggleMinimize} data-testid="timeclock-minimize-btn">
                                {minimized ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>

                    {!minimized && (
                        <>
                            {/* Status */}
                            <div className="flex items-center gap-2">
                                <Badge variant={shiftIn ? 'default' : 'secondary'} data-testid="shift-status-badge">
                                    {shiftIn ? 'Clocked In' : 'Clocked Out'}
                                </Badge>
                                {lunchIn && (
                                    <Badge variant="outline" className="border-amber-500 text-amber-600" data-testid="lunch-status-badge">
                                        On Lunch
                                    </Badge>
                                )}
                            </div>

                            {/* Elapsed */}
                            {elapsed && (
                                <div className="text-2xl font-mono font-bold text-center tracking-wider" data-testid="elapsed-time">
                                    {elapsed}
                                </div>
                            )}

                            {/* Shift buttons */}
                            <div className="grid grid-cols-2 gap-2">
                        {!shiftIn ? (
                            <Button
                                size="sm"
                                className="col-span-2"
                                onClick={() => handlePunch('shift', 'in')}
                                disabled={punching}
                                data-testid="clock-in-btn"
                            >
                                <LogIn className="w-4 h-4 mr-1" />
                                Clock In
                            </Button>
                        ) : (
                            <>
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handlePunch('shift', 'out')}
                                    disabled={punching || lunchIn}
                                    data-testid="clock-out-btn"
                                >
                                    <LogOut className="w-4 h-4 mr-1" />
                                    Clock Out
                                </Button>
                                {!lunchIn ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handlePunch('lunch', 'in')}
                                        disabled={punching}
                                        data-testid="lunch-start-btn"
                                    >
                                        <Coffee className="w-4 h-4 mr-1" />
                                        Start Lunch
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-amber-500 text-amber-600 hover:bg-amber-500/10"
                                        onClick={() => handlePunch('lunch', 'out')}
                                        disabled={punching}
                                        data-testid="lunch-end-btn"
                                    >
                                        <Coffee className="w-4 h-4 mr-1" />
                                        End Lunch
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                        </>
                    )}
                </CardContent>
            </Card>

            <PinVerifyDialog
                open={pinDialogOpen}
                onOpenChange={setPinDialogOpen}
                onVerified={onPinVerified}
            />
        </>
    );
}
